import { useQuery } from '@tanstack/react-query';
import { DollarSign, Database, Activity, TrendingUp, BarChart3, Package, Server } from 'lucide-react';
import Card from '../components/Card';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import { costsApi } from '../api/costs';
import { Link } from 'react-router-dom';

// Helper function to safely format cost values
const formatCost = (value: number | string | null | undefined): string => {
  const num = typeof value === 'string' ? parseFloat(value) : (value || 0);
  return isNaN(num) ? '0.0000' : num.toFixed(4);
};

export default function StatisticsPage() {
  const { data: summary, isLoading, error } = useQuery({
    queryKey: ['costs', 'summary'],
    queryFn: () => costsApi.getSummary(),
    refetchInterval: 30000, // Обновляем каждые 30 секунд
  });

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message="Ошибка загрузки статистики" />;
  if (!summary) return <ErrorMessage message="Статистика недоступна" />;

  const { overall, by_series, by_provider, by_model } = summary;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Статистика затрат на переводы
        </h1>
        <p className="text-gray-500 dark:text-gray-400">
          Подробная информация о затратах на переводы глав и использовании моделей
        </p>
      </div>

      {/* Overall Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Общая стоимость</div>
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                ${formatCost(overall.total_cost_usd)}
              </div>
            </div>
            <DollarSign className="h-8 w-8 text-blue-500" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Всего токенов</div>
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {overall.total_tokens.toLocaleString()}
              </div>
            </div>
            <Database className="h-8 w-8 text-green-500" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Операций</div>
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {overall.total_operations.toLocaleString()}
              </div>
            </div>
            <Activity className="h-8 w-8 text-purple-500" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Серий / Глав</div>
              <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                {overall.total_series} / {overall.total_chapters}
              </div>
            </div>
            <BarChart3 className="h-8 w-8 text-orange-500" />
          </div>
        </Card>
      </div>

      {/* Token Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Card className="p-6">
          <div className="flex items-center mb-4">
            <TrendingUp className="h-5 w-5 text-gray-400 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Детализация токенов</h2>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">Prompt токены:</span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {overall.total_prompt_tokens.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">Completion токены:</span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {overall.total_completion_tokens.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between items-center pt-2 border-t border-gray-200 dark:border-gray-700">
              <span className="text-sm font-medium text-gray-900 dark:text-white">Всего:</span>
              <span className="text-sm font-bold text-gray-900 dark:text-white">
                {overall.total_tokens.toLocaleString()}
              </span>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center mb-4">
            <Server className="h-5 w-5 text-gray-400 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">По провайдерам</h2>
          </div>
          {by_provider && by_provider.length > 0 ? (
            <div className="space-y-3">
              {by_provider.map((provider, idx) => (
                <div key={idx} className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-700 rounded">
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">{provider.provider}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                      ({provider.operations_count} операций)
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      ${formatCost(provider.total_cost_usd)}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {provider.total_tokens.toLocaleString()} токенов
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
              Нет данных по провайдерам
            </p>
          )}
        </Card>
      </div>

      {/* By Series */}
      {by_series && by_series.length > 0 && (
        <Card title="Статистика по сериям" className="mb-6">
          <div className="sm:hidden space-y-3">
            {by_series.map((series) => (
              <div
                key={series.series_id}
                className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/series/${series.series_id}`}
                      className="text-sm font-semibold text-primary-600 dark:text-primary-400 hover:underline break-words"
                    >
                      {series.series_title || 'Без названия'}
                    </Link>
                    <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                      <div className="text-gray-600 dark:text-gray-300">Стоимость</div>
                      <div className="text-right font-medium text-gray-900 dark:text-white">
                        ${formatCost(series.total_cost_usd)}
                      </div>
                      <div className="text-gray-600 dark:text-gray-300">Токены</div>
                      <div className="text-right font-medium text-gray-900 dark:text-white">
                        {series.total_tokens.toLocaleString()}
                      </div>
                      <div className="text-gray-600 dark:text-gray-300">Операций</div>
                      <div className="text-right font-medium text-gray-900 dark:text-white">
                        {series.operations_count}
                      </div>
                      <div className="text-gray-600 dark:text-gray-300">Глав</div>
                      <div className="text-right font-medium text-gray-900 dark:text-white">
                        {series.chapters_count}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  {series.models_used && series.models_used.length > 0 ? (
                    series.models_used.slice(0, 6).map((model, idx) => (
                      <span
                        key={idx}
                        className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded"
                      >
                        {model.model}
                      </span>
                    ))
                  ) : (
                    <span className="text-xs text-gray-500 dark:text-gray-400">—</span>
                  )}
                  {series.models_used && series.models_used.length > 6 && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      +{series.models_used.length - 6}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="hidden sm:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Серия
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Стоимость
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Токены
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Операций
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Глав
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Модели
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {by_series.map((series) => (
                  <tr key={series.series_id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link
                        to={`/series/${series.series_id}`}
                        className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        {series.series_title || 'Без названия'}
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      ${formatCost(series.total_cost_usd)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {series.total_tokens.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {series.operations_count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {series.chapters_count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-wrap gap-1">
                        {series.models_used && series.models_used.length > 0 ? (
                          series.models_used.slice(0, 3).map((model, idx) => (
                            <span
                              key={idx}
                              className="text-xs px-2 py-0.5 bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded"
                            >
                              {model.model}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-gray-500 dark:text-gray-400">—</span>
                        )}
                        {series.models_used && series.models_used.length > 3 && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            +{series.models_used.length - 3}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* By Model */}
      {by_model && by_model.length > 0 && (
        <Card title="Статистика по моделям" className="mb-6">
          <div className="sm:hidden space-y-3">
            {by_model.map((model, idx) => (
              <div
                key={idx}
                className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4"
              >
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-gray-400" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-gray-900 dark:text-white break-words">
                      {model.model_name}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 break-words">
                      {model.provider}
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">
                    ${formatCost(model.total_cost_usd)}
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                  <div className="text-gray-600 dark:text-gray-300">Операций</div>
                  <div className="text-right font-medium text-gray-900 dark:text-white">
                    {model.operations_count.toLocaleString()}
                  </div>
                  <div className="text-gray-600 dark:text-gray-300">Prompt</div>
                  <div className="text-right font-medium text-gray-900 dark:text-white">
                    {model.prompt_tokens.toLocaleString()}
                  </div>
                  <div className="text-gray-600 dark:text-gray-300">Completion</div>
                  <div className="text-right font-medium text-gray-900 dark:text-white">
                    {model.completion_tokens.toLocaleString()}
                  </div>
                  <div className="text-gray-600 dark:text-gray-300">Всего токенов</div>
                  <div className="text-right font-medium text-gray-900 dark:text-white">
                    {model.total_tokens.toLocaleString()}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="hidden sm:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Модель
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Провайдер
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Операций
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Prompt токены
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Completion токены
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Всего токенов
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Стоимость
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {by_model.map((model, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Package className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {model.model_name}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {model.provider}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {model.operations_count.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {model.prompt_tokens.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {model.completion_tokens.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {model.total_tokens.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      ${formatCost(model.total_cost_usd)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {(!by_series || by_series.length === 0) && (!by_model || by_model.length === 0) && (
        <Card>
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
            Статистика будет доступна после выполнения переводов с отслеживанием затрат
          </p>
        </Card>
      )}
    </div>
  );
}
